import { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Trash2, Lock } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'completed', label: 'Trash picked up' },
  { value: 'recycle', label: 'Recycle picked up' },
  { value: 'missed', label: 'Missed pickup' },
  { value: 'violation', label: 'Violation' },
  { value: 'no_placement', label: 'No placement today' },
];

// Delegated pickup logging: Property (delegated only) → Building → Units.
// Hard-blocked when the agent has no delegation for today.
export const DelegatedPickupEntry = ({ onLogged }) => {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [propertyId, setPropertyId] = useState('');
  const [buildingNumber, setBuildingNumber] = useState('');
  const [units, setUnits] = useState([]);
  const [customUnit, setCustomUnit] = useState('');
  const [status, setStatus] = useState('completed');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/company/my-daily-assignments`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    setLoaded(true);
  }, []);

  useEffect(() => { load(); }, [load]);

  const delegations = (data?.schedules_today || []).filter((s) => s.source === 'delegation');
  const propertyOptions = delegations.flatMap((s) =>
    (s.properties?.length ? s.properties : [{ property_id: (s.property_ids || [])[0], property_name: s.property_name }])
  ).filter((p, i, a) => p.property_id && a.findIndex((x) => x.property_id === p.property_id) === i);

  const activeSchedule = delegations.find((s) => (s.property_ids || []).includes(propertyId));
  const buildings = activeSchedule?.buildings || [];
  const activeBuilding = buildings.find((b) => b.building_number === buildingNumber);
  const delegatedUnits = activeBuilding?.units || [];

  const toggleUnit = (u) => setUnits((prev) => (prev.includes(u) ? prev.filter((x) => x !== u) : [...prev, u]));

  const addCustomUnit = () => {
    const u = customUnit.trim();
    if (!u) return;
    if (!units.includes(u)) setUnits((prev) => [...prev, u]);
    setCustomUnit('');
  };

  const submit = async () => {
    if (!propertyId) { toast.error('Select your delegated property first'); return; }
    if (units.length === 0) { toast.error('Select or enter at least one unit number'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/pickups`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          property_id: propertyId,
          unit_number: units[0],
          unit_numbers: units,
          building_number: buildingNumber || null,
          pickup_type: status,
        }),
      });
      const resp = await res.json();
      if (!res.ok) throw new Error(resp?.detail || 'Failed to log pickup');
      toast.success(resp.message || 'Pickup logged');
      setUnits([]);
      onLogged?.();
    } catch (e) {
      toast.error(e.message);
    } finally { setSaving(false); }
  };

  if (loaded && delegations.length === 0) {
    return (
      <Card className="bg-zinc-900 border-2 p-6" style={{ borderColor: 'rgba(239, 68, 68, 0.5)' }} data-testid="pickup-entry-blocked">
        <h3 className="font-bold text-lg flex items-center text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          <Lock className="h-5 w-5 mr-2 text-red-400" />
          PICKUPS LOCKED
        </h3>
        <p className="text-sm text-zinc-400 mt-2">
          You have no delegation for today. Ask your Crew Leader to assign your route before logging pickups.
        </p>
      </Card>
    );
  }

  return (
    <Card className="bg-zinc-900 border-2 p-6" style={{ borderColor: 'rgba(59, 130, 246, 0.5)' }} data-testid="delegated-pickup-entry-card">
      <h3 className="font-bold text-lg flex items-center text-white mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
        <Trash2 className="h-5 w-5 mr-2 text-blue-400" />
        LOG PICKUP · DELEGATED ROUTE
      </h3>
      <p className="text-xs text-zinc-400 mb-4">
        Select your delegated property, then the building, then the unit numbers.
      </p>

      <div className="space-y-3">
        <div>
          <Label className="text-zinc-300">1 · Delegated property</Label>
          <Select value={propertyId} onValueChange={(v) => { setPropertyId(v); setBuildingNumber(''); setUnits([]); }}>
            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="pickup-property-select">
              <SelectValue placeholder="Select delegated property" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              {propertyOptions.map((p) => (
                <SelectItem key={p.property_id} value={p.property_id}>{p.property_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {propertyId && (
          <div>
            <Label className="text-zinc-300">2 · Building</Label>
            {buildings.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="pickup-building-chips">
                {buildings.map((b) => (
                  <button
                    key={b.building_number}
                    type="button"
                    onClick={() => { setBuildingNumber(b.building_number); setUnits([]); }}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-colors ${
                      buildingNumber === b.building_number
                        ? 'bg-blue-600 border-blue-500 text-white'
                        : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-blue-500'
                    }`}
                    data-testid={`pickup-building-${b.building_number}`}
                  >
                    Bldg {b.building_number}
                  </button>
                ))}
              </div>
            ) : (
              <Input
                value={buildingNumber}
                onChange={(e) => setBuildingNumber(e.target.value)}
                placeholder="Building number"
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                data-testid="pickup-building-input"
              />
            )}
          </div>
        )}

        {propertyId && (
          <div>
            <Label className="text-zinc-300">3 · Units ({units.length} selected)</Label>
            {delegatedUnits.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5" data-testid="pickup-unit-chips">
                {delegatedUnits.map((u) => (
                  <button
                    key={u}
                    type="button"
                    onClick={() => toggleUnit(u)}
                    className={`px-1.5 py-0.5 rounded text-[10px] font-mono border ${
                      units.includes(u)
                        ? 'bg-blue-600 border-blue-500 text-white'
                        : 'bg-zinc-800 border-zinc-700 text-zinc-400'
                    }`}
                    data-testid={`pickup-unit-${u}`}
                  >
                    {u}
                  </button>
                ))}
              </div>
            )}
            <div className="flex gap-2 mt-2">
              <Input
                value={customUnit}
                onChange={(e) => setCustomUnit(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addCustomUnit()}
                placeholder="Unit not listed? Enter unit number"
                className="bg-zinc-800 border-zinc-700 text-white text-xs"
                data-testid="pickup-custom-unit-input"
              />
              <Button type="button" onClick={addCustomUnit} variant="outline" className="border-zinc-600 text-zinc-300 text-xs" data-testid="pickup-add-unit-btn">
                Add
              </Button>
            </div>
          </div>
        )}

        <div>
          <Label className="text-zinc-300">4 · Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="pickup-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button
          onClick={submit}
          disabled={saving || !propertyId || units.length === 0}
          className="w-full bg-blue-600 text-white hover:opacity-90 font-bold"
          data-testid="pickup-submit-btn"
        >
          {saving ? 'Logging…' : `Log ${units.length} unit(s)`}
        </Button>
      </div>
    </Card>
  );
};
